import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { scroller } from 'react-scroll';
import {
  signInWithPopup,
  signOut,
  GoogleAuthProvider,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../firebase-config';
import '../styles/Navbar.css';
import logo from '../logo.png';
import profilePlaceholder from '../Profile.png';

const MenuIcon = ({ className = '' }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CloseIcon = ({ className = '' }) => (
  <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export default function Navbar() {
  const [user, setUser] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const navbarHeight = 72;
  const profileMenuRef = useRef(null);
  const drawerRef = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsub();
  }, []);

  // close menus on outside click / Escape
  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') {
        setShowProfileMenu(false);
        setDrawerOpen(false);
      }
    }
    function handleClick(e) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        /* don't auto-close drawer on body clicks — keep controlled by toggle */
      }
    }
    window.addEventListener('keydown', handleKey);
    window.addEventListener('click', handleClick);
    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('click', handleClick);
    };
  }, []);

  const handleScrollToSection = (section) => {
    if (location.pathname === '/') {
      scroller.scrollTo(section, {
        duration: 700,
        delay: 0,
        smooth: 'easeInOutQuart',
        offset: -navbarHeight,
      });
    } else {
      navigate('/', { state: { scrollToSection: section } });
    }
    // Close mobile drawer once clicked
    setDrawerOpen(false);
  };

  const handleContactClick = () => handleScrollToSection('contact-section');
  const handleAboutClick = () => handleScrollToSection('about-us-section');
  const handleProductsServicesClick = () => handleScrollToSection('products-services-section');
  const handleHomeClick = () => handleScrollToSection('home');

  const handleProfileClick = () => setShowProfileMenu((s) => !s);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setShowProfileMenu(false);
      navigate('/');
    } catch (err) {
      console.error('Error during logout:', err);
    }
  };

  const handleLogin = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      await signInWithPopup(auth, provider);
      setShowProfileMenu(false);
      navigate('/dashboard');
    } catch (err) {
      console.error('Error during login:', err);
    }
  };

  const handleDashboardRedirect = () => {
    setShowProfileMenu(false);
    navigate('/dashboard');
  };

  return (
    <>
      <header className={`navbar ${location.pathname === '/' ? 'home-navbar' : ''}`} style={{ height: navbarHeight }}>
        <div className="navbar-left">
          <Link to="/" className="logo-link" aria-label="Home">
            <img src={logo} alt="Logo" className="logo" />
          </Link>
        </div>

        <nav className="navbar-center" role="navigation" aria-label="Main navigation">
          <ul className="nav-links">
            <li>
              <button className="nav-link" onClick={handleHomeClick} aria-label="Home">
                Home
              </button>
            </li>
            <li>
              <button className="nav-link" onClick={handleAboutClick} aria-label="About">
                About
              </button>
            </li>
            <li>
              <button className="nav-link" onClick={handleProductsServicesClick} aria-label="Products and services">
                Products & Services
              </button>
            </li>
          </ul>
        </nav>

        <div className="navbar-right">
          <button className="btn-contact" onClick={handleContactClick} aria-label="Contact us">Contact Us</button>

          <div className="profile-area" ref={profileMenuRef}>
            <button
              className="profile-button"
              aria-haspopup="true"
              aria-expanded={showProfileMenu}
              onClick={handleProfileClick}
            >
              <img className="profile-img" src={user?.photoURL || profilePlaceholder} alt="Profile" />
              <span className="visually-hidden">Open profile menu</span>
            </button>

            <div className={`profile-menu ${showProfileMenu ? 'open' : ''}`} role="menu" aria-label="Profile options">
              {user ? (
                <>
                  <button className="profile-menu-item" onClick={handleDashboardRedirect} role="menuitem">
                    {/* dashboard icon inline */}
                    <span className="menu-item-icon" aria-hidden>📊</span> Dashboard
                  </button>
                  <button className="profile-menu-item" onClick={handleLogout} role="menuitem">
                    <span className="menu-item-icon" aria-hidden>🚪</span> Logout
                  </button>
                </>
              ) : (
                <button className="profile-menu-item" onClick={handleLogin} role="menuitem">
                  <span className="menu-item-icon" aria-hidden>🔐</span> Login with Google
                </button>
              )}
            </div>
          </div>

          <button
            className="hamburger"
            onClick={() => setDrawerOpen((s) => !s)}
            aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={drawerOpen}
          >
            {drawerOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </header>

      {/* Mobile / small-screen drawer */}
      <aside className={`mobile-drawer ${drawerOpen ? 'open' : ''}`} ref={drawerRef} aria-hidden={!drawerOpen}>
        <div className="drawer-top">
          <div className="drawer-logo">
            <img src={logo} alt="Logo" />
          </div>
          <button className="drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close navigation">
            <CloseIcon />
          </button>
        </div>

        <div className="drawer-links">
          <button className="drawer-link" onClick={handleHomeClick}>Home</button>
          <button className="drawer-link" onClick={handleAboutClick}>About</button>
          <button className="drawer-link" onClick={handleProductsServicesClick}>Products & Services</button>
          <button className="drawer-link" onClick={handleContactClick}>Contact Us</button>
        </div>

        <div className="drawer-footer">
          {user ? (
            <>
              <div className="drawer-user">
                <img src={user.photoURL || profilePlaceholder} alt="Profile" />
                <div className="drawer-user-info">
                  <div className="drawer-user-name">{user.displayName || user.email}</div>
                  <div className="drawer-user-email">{user.email}</div>
                </div>
              </div>
              <div className="drawer-actions">
                <button className="drawer-action" onClick={handleDashboardRedirect}>Dashboard</button>
                <button className="drawer-action danger" onClick={handleLogout}>Logout</button>
              </div>
            </>
          ) : (
            <div className="drawer-actions">
              <button className="drawer-action" onClick={handleLogin}>Sign in with Google</button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
